import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useStartupOverlayActive, isStartupOverlayActive, lockOverlayScroll } from '../overlays/startupOverlayState';
import './carousel.css';

const hasForegroundDialog = () => !!document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]');

// Empty space is an exit target, not part of the swipe surface.
export const CarouselSurface = ({ id, label, open = true, covered = false, onExit, children, plain = false, exitLabel = 'Return to Home' }) => {
  const startupActive = useStartupOverlayActive();
  const visible = open && !startupActive;
  const surface = useRef(null);
  const pointer = useRef(null);
  const exit = useRef(onExit);
  exit.current = onExit;
  useEffect(() => {
    if (!visible) return;
    const previousFocus = document.activeElement;
    const unlock = lockOverlayScroll();
    if (!hasForegroundDialog()) surface.current?.focus({ preventScroll: true });
    return () => {
      unlock();
      if (!isStartupOverlayActive() && !hasForegroundDialog() && previousFocus?.isConnected) previousFocus.focus?.({ preventScroll: true });
    };
  }, [visible]);
  useEffect(() => { pointer.current = null; }, [covered]);
  if (!visible) return null;
  const backdropTap = event => {
    if (covered || hasForegroundDialog() || event.defaultPrevented) return;
    if (event.detail === 0) { exit.current?.(); return; }
    const start = pointer.current;
    pointer.current = null;
    if (start && start.target === event.currentTarget && Math.hypot(event.clientX - start.x, event.clientY - start.y) < 10) exit.current?.();
  };
  const keyboard = event => {
    if (covered || hasForegroundDialog() || event.defaultPrevented || !surface.current?.contains(event.target)) return;
    if (event.key === 'Escape') { event.preventDefault(); exit.current?.(); }
    if (event.key !== 'Tab') return;
    const controls = [...surface.current.querySelectorAll('button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]')]
      .filter(el => !el.closest('[inert]') && el.getClientRects().length);
    const first = controls[0], last = controls[controls.length - 1];
    if (!first) { event.preventDefault(); return; }
    if (event.shiftKey && (document.activeElement === first || document.activeElement === surface.current)) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };
  const zone = position => <button type="button" className="carousel-exit-space" aria-label={exitLabel}
    data-testid={`${id}-exit-${position}`} onClick={backdropTap}
    onPointerDown={event => { pointer.current = { x: event.clientX, y: event.clientY, target: event.currentTarget }; }} />;
  return createPortal(
    <div id={id} ref={surface} role="dialog" aria-modal={covered ? undefined : true} aria-hidden={covered || undefined} inert={covered ? true : undefined} aria-label={label} tabIndex={-1}
      style={covered ? { pointerEvents: 'none' } : undefined}
      data-testid={id} className={`carousel-surface ${plain ? 'carousel-surface--plain' : ''}`} onKeyDown={keyboard}>
      {zone('top')}
      <div className="carousel-surface-content" data-testid={`${id}-content`}>{children}</div>
      {zone('bottom')}
    </div>, document.body
  );
};